import { useEffect } from "react";
import { create } from "zustand";
import type { ChatConversation, ChatMessage, ChatPerson, ChatServerEvent } from "@ide/shared";
import { API_BASE, get } from "@/lib/api";
import { keys, queryClient } from "@/lib/queries";
import { onRefresh } from "@/lib/refresh";
import { alertIncoming } from "./alerts";

/**
 * Conexão de tempo real do chat: uma por aba, aberta enquanto houver alguém logado. As mensagens
 * são gravadas pela API REST; o socket só avisa. Ao reconectar, o cliente busca o que perdeu
 * (`after=<último id>`) — nada se perde numa queda de rede.
 */

export interface MessagesPage {
  messages: ChatMessage[];
  hasMore: boolean;
}

interface ChatState {
  status: "offline" | "connecting" | "online";
  /** Conversa aberta na tela (não conta como não lida). */
  activeId: string | null;
  /** conversa → pessoa → até quando mostrar "digitando…" */
  typing: Record<string, Record<string, { user: ChatPerson; until: number }>>;
  /** conversa → pessoa → última mensagem lida (confirmações de leitura). */
  reads: Record<string, Record<string, number>>;
  setActive(id: string | null): void;
}

export const useChatStore = create<ChatState>((set) => ({
  status: "offline",
  activeId: null,
  typing: {},
  reads: {},
  setActive: (id) => set({ activeId: id }),
}));

function socketUrl(): string {
  const base = new URL(API_BASE, window.location.href);
  base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
  base.pathname = `${base.pathname.replace(/\/$/, "")}/chat/ws`;
  return base.toString();
}

// ───────────── Cache (react-query) ─────────────

export function upsertMessage(message: ChatMessage) {
  queryClient.setQueryData<MessagesPage>(keys.messages(message.conversationId), (page) => {
    if (!page) return page;
    const i = page.messages.findIndex(
      (m) => m.id === message.id || (message.clientId !== null && m.clientId === message.clientId),
    );
    if (i >= 0) {
      const next = [...page.messages];
      next[i] = message;
      return { ...page, messages: next };
    }
    return { ...page, messages: [...page.messages, message].sort((a, b) => a.id - b.id) };
  });
}

/** Tira a mensagem da conversa (apagada): some sem deixar rastro. */
export function removeMessage(conversationId: string, messageId: number) {
  queryClient.setQueryData<MessagesPage>(keys.messages(conversationId), (page) =>
    page ? { ...page, messages: page.messages.filter((m) => m.id !== messageId) } : page,
  );
}

/** Online/offline de quem conversa com você, na hora (lista de conversas e cabeçalho). */
function onPresence(userId: string, online: boolean, lastSeenAt: string | null) {
  queryClient.setQueryData<{ conversations: ChatConversation[]; unread: number }>(
    keys.conversations,
    (data) =>
      data
        ? {
            ...data,
            conversations: data.conversations.map((c) =>
              c.peer?.id === userId ? { ...c, peer: { ...c.peer, online, lastSeenAt } } : c,
            ),
          }
        : data,
  );
  for (const [key, detail] of queryClient.getQueriesData<{
    conversation: ChatConversation;
    members: Array<{ id: string; online: boolean }>;
  }>({ queryKey: ["chat", "conversation"] })) {
    if (!detail?.members.some((m) => m.id === userId)) continue;
    queryClient.setQueryData(key, {
      ...detail,
      conversation:
        detail.conversation.peer?.id === userId
          ? { ...detail.conversation, peer: { ...detail.conversation.peer, online, lastSeenAt } }
          : detail.conversation,
      members: detail.members.map((m) => (m.id === userId ? { ...m, online } : m)),
    });
  }
}

function onNewMessage(message: ChatMessage, myId: string | null) {
  upsertMessage(message);
  const active = useChatStore.getState().activeId === message.conversationId && !document.hidden;
  const mine = message.sender?.id === myId;
  let known = false;
  queryClient.setQueryData<{ conversations: ChatConversation[]; unread: number }>(
    keys.conversations,
    (data) => {
      if (!data) return data;
      const conv = data.conversations.find((c) => c.id === message.conversationId);
      if (!conv) return data;
      known = true;
      const bump = !mine && !active && message.kind !== "system" ? 1 : 0;
      const updated: ChatConversation = {
        ...conv,
        lastMessage: message,
        lastMessageAt: message.createdAt,
        unread: conv.unread + bump,
        archived: false,
      };
      return {
        conversations: [updated, ...data.conversations.filter((c) => c.id !== conv.id)],
        unread: data.unread + (conv.muted ? 0 : bump),
      };
    },
  );
  // Conversa nova (alguém escreveu pela primeira vez): recarrega a lista.
  if (!known) void queryClient.invalidateQueries({ queryKey: keys.conversations });
  if (!mine && !active) void queryClient.invalidateQueries({ queryKey: keys.chatUnread });
  // Sino, notificação do sistema e balão: só se a conversa não estiver à vista (janela em foco).
  const viewing = useChatStore.getState().activeId === message.conversationId && document.hasFocus();
  if (!mine) void alertIncoming(message, viewing);
  // Quem escreveu parou de digitar.
  if (message.sender) clearTyping(message.conversationId, message.sender.id);
}

function clearTyping(conversationId: string, userId: string) {
  useChatStore.setState((s) => {
    const conv = { ...(s.typing[conversationId] ?? {}) };
    delete conv[userId];
    return { typing: { ...s.typing, [conversationId]: conv } };
  });
}

// ───────────── Conexão ─────────────

let ws: WebSocket | null = null;
let users = 0;
let retry = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let myId: string | null = null;

function handle(event: ChatServerEvent) {
  switch (event.type) {
    case "ready":
      myId = event.userId;
      useChatStore.setState({ status: "online" });
      void catchUp();
      break;
    case "message.new":
      onNewMessage(event.message, myId);
      break;
    case "message.updated":
      upsertMessage(event.message);
      void queryClient.invalidateQueries({ queryKey: keys.conversations });
      break;
    case "message.removed":
      removeMessage(event.conversationId, event.messageId);
      void queryClient.invalidateQueries({ queryKey: keys.conversations });
      break;
    case "presence":
      onPresence(event.userId, event.online, event.lastSeenAt);
      break;
    case "conversation.updated":
      void queryClient.invalidateQueries({ queryKey: keys.conversation(event.conversationId) });
      void queryClient.invalidateQueries({ queryKey: keys.conversations });
      break;
    case "conversation.removed":
      queryClient.removeQueries({ queryKey: keys.messages(event.conversationId) });
      void queryClient.invalidateQueries({ queryKey: keys.conversations });
      break;
    case "read":
      useChatStore.setState((s) => ({
        reads: {
          ...s.reads,
          [event.conversationId]: {
            ...(s.reads[event.conversationId] ?? {}),
            [event.userId]: event.messageId,
          },
        },
      }));
      if (event.userId === myId) void queryClient.invalidateQueries({ queryKey: keys.chatUnread });
      break;
    case "typing":
      useChatStore.setState((s) => ({
        typing: {
          ...s.typing,
          [event.conversationId]: {
            ...(s.typing[event.conversationId] ?? {}),
            [event.user.id]: { user: event.user, until: Date.now() + 4_000 },
          },
        },
      }));
      break;
    case "pong":
      break;
  }
}

/** Depois de reconectar: busca o que chegou enquanto estava offline nas conversas abertas em cache. */
async function catchUp() {
  void queryClient.invalidateQueries({ queryKey: keys.conversations });
  void queryClient.invalidateQueries({ queryKey: keys.chatUnread });
  const pages = queryClient.getQueriesData<MessagesPage>({ queryKey: ["chat", "messages"] });
  for (const [key, page] of pages) {
    const id = key[2] as string;
    const last = page?.messages.at(-1)?.id;
    if (!page || last === undefined) continue;
    try {
      const fresh = await get<MessagesPage>(`/chat/conversations/${id}/messages?after=${last}&limit=100`);
      for (const m of fresh.messages) upsertMessage(m);
    } catch {
      // Conversa removida ou sem acesso: some do cache.
      queryClient.removeQueries({ queryKey: key });
    }
  }
}

function connect() {
  if (ws || users === 0) return;
  useChatStore.setState({ status: "connecting" });
  const sock = new WebSocket(socketUrl());
  ws = sock;
  sock.onopen = () => {
    retry = 0;
  };
  sock.onmessage = (e) => {
    // Um socket antigo (fechado ao trocar de tela) não entrega mais nada: evita eventos em dobro.
    if (ws !== sock) return;
    try {
      handle(JSON.parse(String(e.data)) as ChatServerEvent);
    } catch {
      // quadro inválido: ignora
    }
  };
  sock.onclose = () => {
    // O fechamento de um socket antigo chega depois que o novo já abriu: não mexe no novo.
    if (ws !== sock) return;
    ws = null;
    useChatStore.setState({ status: "offline" });
    if (users === 0) return;
    // Reconexão com espera crescente (até 30 s) e um pouco de aleatoriedade.
    const delay = Math.min(30_000, 1_000 * 2 ** retry++) * (0.75 + Math.random() * 0.5);
    timer = setTimeout(connect, delay);
  };
}

/** Atualizar: reconecta já (sem esperar a próxima tentativa) e busca o que chegou. */
export function reconnectChat() {
  if (users === 0) return;
  if (ws) return void catchUp();
  if (timer) clearTimeout(timer);
  retry = 0;
  connect();
}

export function sendTyping(conversationId: string) {
  if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "typing", conversationId }));
}

/** Mantém o socket aberto enquanto algum componente montado precisar (app shell, workspace). */
export function useChatSocket(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    users++;
    connect();
    const offRefresh = onRefresh(reconnectChat);
    const onVisible = () => {
      if (!document.hidden && !ws) connect();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      offRefresh();
      users--;
      if (users === 0) {
        if (timer) clearTimeout(timer);
        ws?.close();
        ws = null;
      }
    };
  }, [enabled]);
}

/** Título da aba com o total de não lidas: "(3) Kompil". */
export function useUnreadTitle(unread: number) {
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, "");
    document.title = unread > 0 ? `(${unread > 99 ? "99+" : unread}) ${base}` : base;
  }, [unread]);
}
